// Manager-only firm-wide overview. GET /api/v1/ops/clients.
// Super-users additionally see a Credentials column with per-account status
// + a link to /admin/accounts/<id>/credentials for entry.

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Kicker } from '../../components/shell';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';

interface AccountRow {
  id: string;
  venue: string;
  nickname: string;
  venue_account_name: string;
  has_credentials: boolean;
  key_last_four: string | null;
  credentials_updated_at: string | null;
  credential_status: 'active' | 'needs_reauth' | 'disabled' | null;
  credential_last_error_at: string | null;
}

interface OpsClient {
  id: string;
  display_name: string;
  legal_name: string;
  status: string;
  onboarded_at: string;
  current_period: { label: string; status: string; closes: string | null };
  totals: {
    aum: string;
    aum_prev: string;
    mtd_pct: number;
    ytd_pct: number;
    itd_pct: number;
    pending_deposits_total: string;
    data_age_seconds: number | null;
    staleness_warn_seconds: number;
  };
  accounts: AccountRow[];
}

const usd0 = (s: string) =>
  Number(s).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const pct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`;

const formatAge = (s: number): string => {
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

export default function Clients() {
  const { user } = useAuth();
  const showCredentials = !!user?.is_superuser;
  const [rows, setRows] = useState<OpsClient[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [closing, setClosing] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await api<OpsClient[]>('/ops/clients'));
    } catch {
      setError('Failed to load clients.');
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const closePeriod = async (clientId: string, name: string, periodLabel: string) => {
    if (!window.confirm(
      `Close the ${periodLabel} period for ${name}? This issues an immutable statement, applies pending cashflows at the boundary, and opens the next month. Blocked if recon flags exist.`
    )) return;
    setClosing(clientId);
    try {
      const result = await api<{ statement_id: string; statement_version: number }>(
        `/ops/clients/${clientId}/period-close`,
        { method: 'POST' },
      );
      window.alert(`Issued statement v${result.statement_version} (${result.statement_id.slice(0, 8)}…). Opening next period.`);
      await load();
    } catch (e: any) {
      const detail = e?.payload?.detail;
      window.alert(`Could not close: ${typeof detail === 'string' ? detail : 'unknown error'}`);
    } finally {
      setClosing(null);
    }
  };

  return (
    <div>
      <div className="section-head" style={{
        marginBottom: 32, display: 'flex', justifyContent: 'space-between',
        alignItems: 'flex-end', gap: 24, flexWrap: 'wrap',
      }}>
        <div>
          <Kicker>01 · Investors · Firm-wide</Kicker>
          <h1 className="page-title">All <em>investors.</em></h1>
        </div>
        {/* 16.d.3.5 — the standalone "+ Onboard client" (SMA) CTA is retired:
            fund-only going forward, new onboarding flows through the fund-first
            path (/fund/new → investors + subscription, + invite-to-portal). The
            SMA ClientOnboard code/route is kept on disk (reversible), just not
            linked from the manager UI. */}
      </div>

      {error && (
        <p style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--accent)' }}>
          {error}
        </p>
      )}

      {!rows && !error && (
        <p style={{
          fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.16em',
          textTransform: 'uppercase', color: 'var(--ink-3)',
        }}>
          Loading investors…
        </p>
      )}

      {rows && rows.length === 0 && (
        <p style={{ fontFamily: 'var(--serif)', color: 'var(--ink-2)' }}>
          No investors yet.
        </p>
      )}

      {rows && rows.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--ink)' }}>
              <Th>Investor</Th>
              <Th>Status</Th>
              <Th align="right">AUM</Th>
              <Th align="right">MTD</Th>
              <Th>Period</Th>
              <Th align="right">Pending</Th>
              {showCredentials && <Th>Credentials</Th>}
              <Th align="right">Action</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} style={{ borderBottom: 'var(--hairline)' }}>
                <Td>
                  <Link
                    to={`/dashboard?client_id=${r.id}`}
                    style={{ color: 'var(--ink)', textDecoration: 'none' }}
                  >
                    <span style={{ fontFamily: 'var(--serif)', fontSize: 18 }}>
                      <em>{r.display_name}</em>
                    </span>
                    <div style={{
                      fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.14em',
                      textTransform: 'uppercase', color: 'var(--ink-3)', marginTop: 4,
                    }}>
                      {r.legal_name}
                    </div>
                  </Link>
                </Td>
                <Td>
                  <span style={{
                    fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.14em',
                    textTransform: 'uppercase', color: 'var(--ink-2)',
                  }}>
                    {r.status}
                  </span>
                </Td>
                <Td align="right" mono>
                  {usd0(r.totals.aum)}
                </Td>
                <Td align="right" mono>
                  <span style={{ color: r.totals.mtd_pct >= 0 ? 'var(--accent)' : 'var(--ink-2)' }}>
                    {pct(r.totals.mtd_pct)}
                  </span>
                </Td>
                <Td>
                  <span style={{ fontFamily: 'var(--serif)', fontStyle: 'italic' }}>
                    {r.current_period.label || '—'}
                  </span>
                  {r.totals.data_age_seconds != null
                    && r.totals.data_age_seconds > r.totals.staleness_warn_seconds && (
                    <div
                      title={`Latest snapshot is ${formatAge(r.totals.data_age_seconds)} old`}
                      style={{
                        marginTop: 4, display: 'inline-block',
                        padding: '1px 5px',
                        border: '0.5pt solid #6b2a2a', color: '#6b2a2a',
                        fontFamily: 'var(--mono)', fontSize: 9,
                        letterSpacing: '0.14em', textTransform: 'uppercase',
                      }}
                    >
                      stale · {formatAge(r.totals.data_age_seconds)}
                    </div>
                  )}
                </Td>
                <Td align="right" mono>
                  {Number(r.totals.pending_deposits_total) > 0
                    ? usd0(r.totals.pending_deposits_total)
                    : '—'}
                </Td>
                {showCredentials && (
                  <Td>
                    {r.accounts.length === 0 ? (
                      <span style={{ color: 'var(--ink-3)' }}>—</span>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {r.accounts.map(a => {
                          // On-chain accounts don't have per-Account API
                          // keys — Etherscan uses one firm-wide env key.
                          // Render '—' instead of an actionable link.
                          if (a.venue === 'onchain') {
                            return (
                              <span key={a.id} style={{
                                fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.06em',
                                color: 'var(--ink-3)',
                              }} title={a.nickname || a.venue}>
                                {a.nickname || a.venue} · —
                              </span>
                            );
                          }
                          return (
                            <Link
                              key={a.id}
                              to={`/admin/accounts/${a.id}/credentials`}
                              style={{
                                textDecoration: 'none', color: 'var(--ink)',
                                fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.06em',
                              }}
                              title={a.nickname || a.venue}
                            >
                              <span style={{ color: 'var(--ink-3)' }}>
                                {a.venue_account_name || a.nickname || a.venue}
                                {' · '}
                              </span>
                              {a.has_credentials ? (
                                <>
                                  <span style={{
                                    color: a.credential_status === 'needs_reauth'
                                      ? 'var(--oxblood, #6b2a2a)'
                                      : 'var(--accent)',
                                  }}>
                                    ****{a.key_last_four} →
                                  </span>
                                  {a.credential_status === 'needs_reauth' && (
                                    <span style={{
                                      marginLeft: 6, padding: '1px 5px',
                                      border: '0.5pt solid #6b2a2a',
                                      color: '#6b2a2a',
                                      fontFamily: 'var(--mono)', fontSize: 9,
                                      letterSpacing: '0.14em', textTransform: 'uppercase',
                                    }} title="API returned 401/403. Upload a fresh key.">
                                      needs re-auth
                                    </span>
                                  )}
                                </>
                              ) : (
                                <span style={{ color: 'var(--oxblood, var(--accent))' }}>Not set →</span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </Td>
                )}
                <Td align="right">
                  {r.current_period.status === 'open' ? (
                    <button
                      className="btn-link"
                      onClick={() => void closePeriod(r.id, r.display_name, r.current_period.label)}
                      disabled={closing === r.id}
                    >
                      {closing === r.id ? 'Closing…' : 'Close period →'}
                    </button>
                  ) : <span style={{ color: 'var(--ink-3)' }}>—</span>}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// --- tiny editorial table cells ---
function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th style={{
      textAlign: align, padding: '12px 16px',
      fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.16em',
      textTransform: 'uppercase', color: 'var(--ink-3)', fontWeight: 400,
    }}>
      {children}
    </th>
  );
}
function Td({
  children, align = 'left', mono = false,
}: { children: React.ReactNode; align?: 'left' | 'right'; mono?: boolean }) {
  return (
    <td style={{
      textAlign: align, padding: '20px 16px',
      fontFamily: mono ? 'var(--mono)' : 'inherit',
      fontVariantNumeric: mono ? 'tabular-nums' : 'normal',
      fontSize: mono ? 14 : undefined,
      verticalAlign: 'top',
    }}>
      {children}
    </td>
  );
}
